import { useCallback, useEffect, useRef, useState } from 'react';

import { GPMOTION_METHOD_CONFIG } from '@/constant';
import type {
  EventListenerRef,
  GPMotionConfig,
  UseGPMotionProps,
} from '@/features/method-tester/types/gpMotionTypes';
import { capitalizeFirst } from '@/utils';

// Returns the GPMotion config for a given method name, or a default config if not found.
const getGPMotionConfig = (methodName: string): GPMotionConfig => {
  const lower = methodName.toLowerCase();
  if (GPMOTION_METHOD_CONFIG[lower]) {
    return GPMOTION_METHOD_CONFIG[lower];
  }
  return {
    method: `start${capitalizeFirst(methodName)}`,
    event: `GPMotion.Event.${lower}`,
    key: methodName,
    stopMethod: `stop${capitalizeFirst(methodName)}`,
  };
};

export function useGPMotion({
  getSubmissionData,
  invokeSDKActionV1,
  clearResponse,
}: UseGPMotionProps) {
  const [streamData, setStreamData] = useState<Record<string, any>>({});
  const eventListenerRef = useRef<EventListenerRef>(null);
  function updateStreamData(key: string, detail: any) {
    setStreamData((prev) => {
      const prevData = prev[key];
      if (prevData === detail) return prev;

      return {
        ...prev,
        [key]: detail,
      };
    });
  }

  const removeCurrentEventListener = (
    eventListenerRef: React.MutableRefObject<EventListenerRef>
  ) => {
    if (eventListenerRef.current) {
      document.removeEventListener(
        eventListenerRef.current.eventName,
        eventListenerRef.current.handler as EventListener
      );
      eventListenerRef.current = null;
    }
  };
  // Submits the SDK action for the current method.
  const handleSubmit = useCallback(async () => {
    const submissionData = getSubmissionData();
    clearResponse();
    const { method, params } = getGPMotionConfig(submissionData.methodName);
    await invokeSDKActionV1(
      submissionData.className,
      method,
      params !== undefined ? params : submissionData.params
    );
  }, [getSubmissionData, clearResponse, invokeSDKActionV1]);

  // Starts listening for a GPMotion event and updates stream data.
  const handleStart = useCallback(async () => {
    const submissionData = getSubmissionData();
    const { event, key } = getGPMotionConfig(submissionData.methodName);

    // Vibrate is a one-off action, not a stream
    if (submissionData.methodName.toLowerCase() === 'vibrate') {
      await handleSubmit();
      return;
    }

    removeCurrentEventListener(eventListenerRef);

    const eventName =
      event ||
      `GPMotion.Event.${submissionData.methodName.replace(/^start/, '').toLowerCase()}`;
    const dataKey = key || submissionData.methodName;

    const handler = (e: Event) => {
      if (window?.flutter_inappwebview) {
        updateStreamData(dataKey, (e as CustomEvent).detail);
      } else {
        updateStreamData(dataKey, (e as Event & { detail: unknown }).detail);
      }
    };

    eventListenerRef.current = { eventName, handler };
    document.addEventListener(eventName, handler as EventListener);
    await handleSubmit();
  }, [getSubmissionData, handleSubmit]);

  // Stops the GPMotion event listener and clears stream data for the method.
  const handleStop = useCallback(async () => {
    const submissionData = getSubmissionData();
    removeCurrentEventListener(eventListenerRef);
    const { stopMethod, stopParams } = getGPMotionConfig(
      submissionData.methodName
    );
    if (stopMethod) {
      await invokeSDKActionV1(
        submissionData.className,
        stopMethod,
        stopParams !== undefined ? stopParams : submissionData.params
      );
    }
    setStreamData((prev) => {
      if (
        Array.isArray(prev[submissionData.methodName]) &&
        prev[submissionData.methodName].length === 0
      )
        return prev;
      return {
        ...prev,
        [submissionData.methodName]: [],
      };
    });
  }, [getSubmissionData, invokeSDKActionV1]);

  // Cleanup event listener on unmount
  useEffect(() => {
    return () => {
      removeCurrentEventListener(eventListenerRef);
    };
  }, []);

  // Resets all GPMotion stream data and removes event listener.
  const resetStreamData = useCallback(() => {
    setStreamData({});
    removeCurrentEventListener(eventListenerRef);
  }, []);

  return {
    streamData,
    handleStart,
    handleStop,
    resetStreamData,
  };
}
